import { makeAutoObservable, runInAction } from "mobx";
import * as api from "../api";
import { hasRecurringTimeSlots } from "../utils/timeSlots";

const makeInitialChosenTicketsState = (tickets = []) => {
    return tickets.reduce((initialState, ticket) => {
        initialState[ticket._id] = { amount: 0, info: {} }
        return initialState
    }, {})
}

class CheckoutStore {
    loading = false;
    activeStep = 0;
    initPromise = null;
    chosenTickets = null;
    initialized = false;
    splitPayment = null;
    prepareSplitPayment = false;
    splitPaymentIframeLink = null;
    splitPaymentError = null;

    constructor() {
        makeAutoObservable(this);
    }

    get totalChosenTickets() {
        if (!this.chosenTickets) return 0;
        return Object.values(this.chosenTickets).reduce((acc, ticket) => acc + ticket.amount, 0);
    }

    init = (event, tickets) => {
        const eventChanged = this.event?._id !== event?._id;
        this.event = event;
        // Only reset chosen tickets on first init or when switching to a different event
        if (!this.initialized || eventChanged) {
            this.chosenTickets = makeInitialChosenTicketsState(tickets);
        } else if (tickets?.length) {
            // Rehydrate: backfill new ticket IDs while preserving existing user selections
            const freshState = makeInitialChosenTicketsState(tickets);
            for (const ticketId of Object.keys(freshState)) {
                if (this.chosenTickets[ticketId]) {
                    freshState[ticketId] = this.chosenTickets[ticketId];
                }
            }
            this.chosenTickets = freshState;
        }
        if (!this.initialized || eventChanged || (this.activeStep !== 0 && this.activeStep !== 1 && this.activeStep !== 2)) {
            this.activeStep = hasRecurringTimeSlots(event) ? 10 : 0;
        }
        this.initialized = true;
    }

    setChosenTickets = (chosenTickets) => {
        this.chosenTickets = chosenTickets;
    }

    setActiveStep = async (step) => {
        try {
            if (this.activeStep === step) return;
            if (step === 1 || step === 2) {
                await this.checkEventTicketsLeft();
            }
            runInAction(() => {
                this.activeStep = step;
            });
        } catch (error) {
            console.error(error);
        }
    }

    reset = () => {
        this.activeStep = 0;
        this.chosenTickets = null;
        this.initialized = false;
        this.splitPayment = null;
        this.prepareSplitPayment = false;
        this.splitPaymentIframeLink = null;
        this.splitPaymentError = null;
    }

    getSplitPaymentIframeLink = async (data) => {
        this.prepareSplitPayment = true;
        this.splitPaymentError = null;
        try {
            const res = await api.createDivideOrder({ data });
            runInAction(() => {
                this.splitPaymentIframeLink = res.data.link;
            })
            this.setActiveStep(2);
        } catch (error) {
            console.error(error);
            this.splitPaymentError = error;
        } finally {
            runInAction(() => {
                this.prepareSplitPayment = false;
            });
        }
    }

    checkEventTicketsLeft = async () => {
        if (this.event.isSeatsEvent) return true; // no need to check tickets for seating events
        if (this.event.isWaitingListEnabled) return true; // no need to check tickets for waiting list events
        this.loading = true;
        try {
            const { data: tickets } = await api.fetchEventTickets({ accountId: this.event.accountId, eventId: this.event._id });
            if (!tickets.length) {
                return;
            }
            // TODO:
            const { eventCapacityLeft } = tickets[0];
            // A time-slot event's capacity lives on the slot, not on the event. The event-wide
            // maxCapacity/maxCapacityLeft pair means "N for the whole event across every date",
            // which describes nothing for a recurring slot event — and its counter drifts negative
            // because rejected/refunded orders never release it, which would block the buyer here
            // with no_tickets_left on an event that still has free spots (#947). The backend applies
            // the same rule on the purchase path (#832) and bypasses is_sold_out/ticketsLeft for
            // time-slot events, but not eventCapacityLeft — so gate it out here.
            const isTimeSlotEvent = this.event.timeSlotConfig?.enabled === true;
            const maxCapacityGateEnabled = this.event.maxCapacityEnabled && !isTimeSlotEvent;
            if (maxCapacityGateEnabled && !isNaN(eventCapacityLeft) && eventCapacityLeft <= 0) {
                // no more tickets left
                this.setActiveStep(7);
                throw new Error('no_tickets_left');
            }
            const totalChosenTickets = Object.values(this.chosenTickets).reduce((acc, ticket) => acc + ticket.amount, 0);
            if (maxCapacityGateEnabled && !isNaN(eventCapacityLeft) && totalChosenTickets > eventCapacityLeft) {
                // too many tickets chosen
                this.setActiveStep(8);
                throw new Error('too_many_tickets_chosen');
            }
            // validate that there are enough tickets left for the chosen tickets
            // use a for loop to break out of the loop when there are no more tickets left of a certain type
            for (let chosenTicketId in this.chosenTickets) {
                const chosenTicket = this.chosenTickets[chosenTicketId];
                const chosenTicketAmount = chosenTicket.amount;
                if (!chosenTicketAmount) continue;
                const ticket = tickets.find((t) => t._id === chosenTicketId);
                if (!ticket || ticket.is_sold_out) {
                    this.setActiveStep(8);
                    throw new Error('ticket_sold_out');
                }
                // if there are not enough tickets left, set the active step to 7
                const availableTickets = maxCapacityGateEnabled ? Math.min(eventCapacityLeft, ticket.ticketsLeft) : ticket.ticketsLeft;
                if (chosenTicketAmount > availableTickets) {
                    this.setActiveStep(8);
                    throw new Error('ticket_sold_out');
                }
            }
        } catch (error) {
            console.error(error);
            throw error;
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }
}

export default new CheckoutStore();
